import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './RaffleDrawPage.css';

interface Raffle {
  id: string;
  title: string;
  description: string;
  type: 'numbers' | 'names' | 'animals' | 'custom';
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  total_spots: number;
  spot_price: number;
  prize_description: string;
  draw_date: string;
  winner_numbers?: number[];
  winner_user_id?: string;
  creator_id: string;
  created_at: string;
  updated_at: string;
}

interface Participation {
  id: string;
  raffle_id: string;
  user_id: string;
  spot_numbers: number[];
  payment_status: 'pending' | 'paid' | 'cancelled' | 'approved' | 'rejected';
  user?: {
    email: string;
    full_name: string;
  };
}

export const RaffleDrawPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [raffle, setRaffle] = useState<Raffle | null>(null);
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [winnerNumber, setWinnerNumber] = useState<number | null>(null);
  const [winnerParticipation, setWinnerParticipation] = useState<Participation | null>(null);
  const [animatingNumbers, setAnimatingNumbers] = useState<number[]>([]);
  const [currentAnimatedNumber, setCurrentAnimatedNumber] = useState<number | null>(null);
  const [drawComplete, setDrawComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchRaffleData();
    }
  }, [id]);

  useEffect(() => {
    if (isLive && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isLive && countdown === 0) {
      performDraw();
    }
  }, [isLive, countdown]);

  const fetchRaffleData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch raffle details
      const { data: raffleData, error: raffleError } = await supabase
        .from('raffles')
        .select('*')
        .eq('id', id)
        .single();

      if (raffleError) throw raffleError;
      if (!raffleData) throw new Error('Raffle not found');

      // Check if user is the creator
      if (raffleData.creator_id !== user?.id) {
        setError('You are not authorized to draw this raffle');
        return;
      }

      // Check if raffle is already completed
      if (raffleData.status === 'completed') {
        setError('This raffle has already been drawn');
        setWinnerNumber(raffleData.winner_numbers?.[0] || null);
        setDrawComplete(true);
      }

      setRaffle(raffleData);

      // Fetch all approved participations
      const { data: participationsData, error: participationsError } = await supabase
        .from('raffle_participations')
        .select(`
          *,
          user:profiles!user_id(email, full_name)
        `)
        .eq('raffle_id', id)
        .in('payment_status', ['approved', 'paid']);

      if (participationsError) throw participationsError;

      setParticipations(participationsData || []);
    } catch (err: any) {
      console.error('Error fetching raffle data:', err);
      setError(err.message || 'Failed to load raffle');
    } finally {
      setLoading(false);
    }
  };

  const getAllSoldNumbers = (): number[] => {
    const numbers: number[] = [];
    participations.forEach(p => {
      numbers.push(...p.spot_numbers);
    });
    return numbers.sort((a, b) => a - b);
  };

  const startDraw = () => {
    const soldNumbers = getAllSoldNumbers();
    if (soldNumbers.length === 0) {
      alert('No tickets have been sold yet!');
      return;
    }

    setIsLive(true);
    setCountdown(10);

    // Broadcast that draw is starting
    broadcastDrawEvent('draw_starting', { countdown: 10 });
  };

  const performDraw = async () => {
    setDrawing(true);
    const soldNumbers = getAllSoldNumbers();
    
    // Animate random numbers for effect
    let animationCount = 0;
    const maxAnimations = 20;
    
    const animateNumbers = () => {
      if (animationCount < maxAnimations) {
        const randomIndex = Math.floor(Math.random() * soldNumbers.length);
        const randomNumber = soldNumbers[randomIndex];
        setCurrentAnimatedNumber(randomNumber);
        setAnimatingNumbers(prev => [...prev.slice(-4), randomNumber]);
        animationCount++;
        setTimeout(animateNumbers, 150);
      } else {
        // Select actual winner
        selectWinner(soldNumbers);
      }
    };

    animateNumbers();
  };

  const selectWinner = async (soldNumbers: number[]) => {
    // Use a deterministic random based on timestamp and raffle ID for transparency
    const seed = new Date().getTime() + id!.charCodeAt(0);
    const randomIndex = seed % soldNumbers.length;
    const winner = soldNumbers[randomIndex];

    setWinnerNumber(winner);
    setCurrentAnimatedNumber(winner);

    // Find the participation that owns this number
    const winnerParticipation = participations.find(p => 
      p.spot_numbers.includes(winner)
    );

    if (winnerParticipation) {
      setWinnerParticipation(winnerParticipation);

      // Update raffle with winner information
      try {
        const { error } = await supabase
          .from('raffles')
          .update({
            status: 'completed',
            winner_numbers: [winner],
            winner_user_id: winnerParticipation.user_id
          })
          .eq('id', id);

        if (error) throw error;

        // Broadcast winner
        broadcastDrawEvent('winner_selected', {
          winnerNumber: winner,
          winnerName: winnerParticipation.user?.full_name || 'Unknown',
          winnerEmail: winnerParticipation.user?.email
        });

        setDrawComplete(true);
        setDrawing(false);
      } catch (err: any) {
        console.error('Error updating raffle:', err);
        setError('Failed to save winner. Please try again.');
        setDrawing(false);
      }
    }
  };

  const broadcastDrawEvent = (eventType: string, data: any) => {
    // In a real implementation, this would use WebSockets or Server-Sent Events
    // For now, we'll use Supabase real-time features
    console.log('Broadcasting event:', eventType, data);
  };

  const resetDraw = () => {
    setIsLive(false);
    setCountdown(10);
    setWinnerNumber(null);
    setWinnerParticipation(null);
    setAnimatingNumbers([]);
    setCurrentAnimatedNumber(null);
    setDrawing(false);
    setDrawComplete(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="draw-page-loading">
        <div className="spinner"></div>
        <p>Loading raffle data...</p>
      </div>
    );
  }

  if (error && !drawComplete) {
    return (
      <div className="draw-page-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/creator-dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="raffle-draw-page">
      <div className="draw-container">
        <header className="draw-header">
          <h1>Raffle Draw System</h1>
          <div className="raffle-info">
            <h2>{raffle?.title}</h2>
            <p>{raffle?.prize_description}</p>
            <div className="draw-stats">
              <span>Total Spots: {raffle?.total_spots}</span>
              <span>Sold: {getAllSoldNumbers().length}</span>
              <span>Participants: {participations.length}</span>
            </div>
          </div>
        </header>

        <div className="draw-main">
          {!isLive && !drawComplete && (
            <div className="pre-draw">
              <h3>Ready to Draw?</h3>
              <p>Make sure all participants are ready. The draw will be broadcast live.</p>
              <div className="participants-summary">
                <h4>Verified Participants ({participations.length})</h4>
                <div className="participants-grid">
                  {participations.map(p => (
                    <div key={p.id} className="participant-badge">
                      <span className="participant-name">
                        {p.user?.full_name || 'Unknown'}
                      </span>
                      <span className="participant-numbers">
                        {p.spot_numbers.length} numbers
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <button 
                className="start-draw-btn"
                onClick={startDraw}
                disabled={participations.length === 0}
              >
                Start Live Draw
              </button>
            </div>
          )}

          {isLive && !drawing && countdown > 0 && (
            <div className="countdown-section">
              <h2>Draw Starting In...</h2>
              <div className="countdown-number">{countdown}</div>
              <p>Get ready! The winner will be selected randomly.</p>
            </div>
          )}

          {drawing && (
            <div className="drawing-section">
              <h2>Drawing Winner...</h2>
              <div className="number-animation">
                <div className="animated-numbers">
                  {animatingNumbers.map((num, index) => (
                    <div 
                      key={`${num}-${index}`} 
                      className="animated-number"
                      style={{ 
                        animationDelay: `${index * 0.1}s`,
                        opacity: 1 - (index * 0.2)
                      }}
                    >
                      {num.toString().padStart(3, '0')}
                    </div>
                  ))}
                </div>
                {currentAnimatedNumber && (
                  <div className="current-number">
                    {currentAnimatedNumber.toString().padStart(3, '0')}
                  </div>
                )}
              </div>
            </div>
          )}

          {drawComplete && winnerNumber && (
            <div className="winner-section">
              <div className="confetti"></div>
              <h2>🎉 We Have a Winner! 🎉</h2>
              <div className="winner-number">
                {winnerNumber.toString().padStart(3, '0')}
              </div>
              {winnerParticipation && (
                <div className="winner-details">
                  <h3>Congratulations!</h3>
                  <p className="winner-name">
                    {winnerParticipation.user?.full_name || 'Unknown'}
                  </p>
                  <p className="winner-email">
                    {winnerParticipation.user?.email}
                  </p>
                  <p className="prize-info">
                    Prize: {raffle?.prize_description}
                  </p>
                </div>
              )}
              <div className="post-draw-actions">
                <button 
                  className="view-results-btn"
                  onClick={() => navigate(`/raffle/${id}`)}
                >
                  View Public Results
                </button>
                <button 
                  className="back-dashboard-btn"
                  onClick={() => navigate('/creator-dashboard')}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="draw-footer">
          <div className="transparency-info">
            <h4>Transparency Notice</h4>
            <p>This draw is conducted using a transparent random selection algorithm.</p>
            <p>Draw Date & Time: {formatDate(new Date().toISOString())}</p>
            <p>Total Eligible Numbers: {getAllSoldNumbers().length}</p>
            {winnerNumber && (
              <p>Winning Number: {winnerNumber.toString().padStart(3, '0')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};